import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Radio,
  RadioGroup,
  Typography,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { AddressProps } from '~/hooks/useGoogleAddressValidation';
import axiosServices from '~/utils/axios';

type SuggestedAddressModalProps = {
  userAddress: AddressProps | null;
  suggestedAddress: AddressProps | null;
  latitude?: string;
  longitude?: string;
  open: boolean;
  onClose: () => void;
  onConfirm: (address: AddressProps | null, selectedOption: 'user' | 'suggested') => void;
};

export const SuggestedAddressModal: React.FC<SuggestedAddressModalProps> = ({
  userAddress,
  suggestedAddress,
  latitude,
  longitude,
  open,
  onClose,
  onConfirm,
}) => {
  const [mapUrl, setMapUrl] = useState('');

  useEffect(() => {
    if (!latitude || !longitude) return;

    axiosServices
      .get<{ url: string }>('/me/map-url', { params: { lat: latitude, lng: longitude } })
      .then(({ data }) => setMapUrl(data.url))
      .catch((error) => {
        console.error('Failed to load map URL:', error);
      });
  }, [latitude, longitude]);
  const [selectedOption, setSelectedOption] = useState<'user' | 'suggested'>('suggested');
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      PaperProps={{
        sx: {
          width: '500px',
          maxWidth: '90vw',
        },
      }}
    >
      <DialogTitle
        sx={{
          fontWeight: 600,
          fontSize: {
            xs: '18px',
            sm: '20px',
            md: '22px',
          },
        }}
      >
        Confirm your address
      </DialogTitle>

      <DialogContent dividers>
        <Typography mb={2}>Review the recommended changes and confirm your address.</Typography>

        <FormControl sx={{ width: '100%' }}>
          <RadioGroup
            value={selectedOption}
            onChange={(e) => setSelectedOption(e.target.value as 'user' | 'suggested')}
          >
            <Box
              sx={{
                border: '1px solid',
                borderColor: selectedOption === 'user' ? 'primary.main' : 'grey.300',
                borderRadius: 2,
                p: 2,
                mb: 2,
                display: 'flex',
                alignItems: 'flex-start',
              }}
            >
              <Radio value="user" checked={selectedOption === 'user'} sx={{ mt: 0.5, mr: 2 }} />

              <Box>
                <Typography variant="subtitle2" fontWeight={500}>
                  What you entered
                </Typography>
                <Typography>
                  {userAddress?.addressLines
                    ?.filter((line) => line && line.trim() !== '')
                    .join(', ')}
                </Typography>
                <Typography>
                  {userAddress?.locality}, {userAddress?.administrativeArea}{' '}
                  {userAddress?.postalCode}
                </Typography>
              </Box>
            </Box>

            <Box
              sx={{
                border: '1px solid',
                borderColor: selectedOption === 'suggested' ? 'primary.main' : 'grey.300',
                borderRadius: 2,
                p: 2,
                display: 'flex',
                alignItems: 'flex-start',
                flexDirection: 'column',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'flex-start', width: '100%' }}>
                <Radio
                  value="suggested"
                  checked={selectedOption === 'suggested'}
                  sx={{ mt: 0.5, mr: 2 }}
                />
                <Box>
                  <Typography variant="subtitle2" fontWeight={500}>
                    Recommended
                  </Typography>
                  <Typography>
                    {suggestedAddress?.addressLines
                      ?.filter((line) => line && line.trim() !== '')
                      .join(', ')}
                  </Typography>
                  <Typography>
                    {suggestedAddress?.locality}, {suggestedAddress?.administrativeArea}{' '}
                    {suggestedAddress?.postalCode}
                  </Typography>
                </Box>
              </Box>
              <Box mt={2} sx={{ borderRadius: 2, overflow: 'hidden', width: '100%' }}>
                <iframe
                  title="Suggested Address Map"
                  height="200"
                  width="100%"
                  style={{ border: 0 }}
                  loading="lazy"
                  allowFullScreen
                  referrerPolicy="no-referrer-when-downgrade"
                  src={mapUrl}
                />
              </Box>
            </Box>
          </RadioGroup>
        </FormControl>
      </DialogContent>

      <DialogActions
        sx={{
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { xs: 'stretch', sm: 'center' },
          justifyContent: 'space-between',
          gap: 1,
          px: 2,
          pb: 2,
        }}
      >
        <Button
          fullWidth
          variant="outlined"
          onClick={onClose}
          sx={{
            fontWeight: 600,
            fontSize: { xs: '0.875rem', sm: '1rem' },
          }}
        >
          BACK
        </Button>
        <Button
          fullWidth
          variant="contained"
          onClick={() => {
            const selected = selectedOption === 'suggested' ? suggestedAddress : userAddress;
            onConfirm(selected, selectedOption);
          }}
          sx={{
            fontWeight: 600,
            fontSize: { xs: '0.875rem', sm: '1rem' },
            ml: { xs: '0 !important', sm: 'auto' },
          }}
        >
          CONFIRM ADDRESS
        </Button>
      </DialogActions>
    </Dialog>
  );
};
